<template>
  <div class="alert-widget d-flex flex-column h-100">
    <div class="form-group mb-2">
      <label class="small text-muted">Target</label>
      <select v-model="target" class="form-control form-control-sm" :disabled="sending">
        <option value="all">All online devices</option>
        <option v-for="n in nodes" :key="n.id" :value="n.id">
          {{ n.name }}{{ n.online ? '' : ' (offline)' }}
        </option>
      </select>
    </div>

    <div class="alert-widget__shortcuts flex-grow-1">
      <button
        v-for="(item, i) in shortcuts"
        :key="i"
        type="button"
        class="btn btn-sm alert-widget__shortcut"
        :style="btnStyle(item)"
        :disabled="sending"
        @click="send(item)"
      >
        <i class="fas" :class="'fa-' + (item.icon || 'bell')"></i>
        {{ item.label }}
      </button>
      <p v-if="!shortcuts.length" class="small text-muted mb-0">
        Belum ada shortcut. Buka settings widget untuk menambah pesan.
      </p>
    </div>

    <button
      type="button"
      class="btn btn-sm btn-outline-danger alert-widget__clear"
      :disabled="sending"
      @click="clearAll"
    >
      <i class="fas" :class="clearing ? 'fa-spinner fa-spin' : 'fa-times'" />
      Clear all
    </button>

    <div v-if="status" class="small mt-2" :class="ok ? 'text-success' : 'text-danger'">{{ status }}</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { normalizeAlertShortcuts, DEFAULT_ALERT_SHORTCUTS, contrastText } from '@/widgets/alertShortcuts'

export default {
  props: {
    config: { type: Object, default: () => ({}) },
    widgetId: { type: String, default: '' },
  },
  data() {
    return {
      target: this.config.target || 'all',
      sending: false,
      clearing: false,
      status: '',
      ok: false,
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes']),
    shortcuts() {
      if (this.config.shortcuts == null) return DEFAULT_ALERT_SHORTCUTS
      return normalizeAlertShortcuts(this.config.shortcuts)
    },
  },
  watch: {
    'config.target'(v) {
      if (v) this.target = v
    },
    target(v) {
      if (!this.widgetId || v === (this.config.target || 'all')) return
      this.updateWidgetConfig({ widgetId: this.widgetId, config: { target: v } })
    },
  },
  methods: {
    ...mapActions('mission', ['sendAlert']),
    ...mapActions('workspace', ['updateWidgetConfig']),
    btnStyle(item) {
      const bg = item.color || '#ffbc34'
      return {
        backgroundColor: bg,
        borderColor: bg,
        color: contrastText(bg),
      }
    },
    async send(item) {
      const text = String(item.message || item.label || '').trim()
      if (!text || this.sending) return
      this.sending = true
      this.status = ''
      try {
        const payload = {
          message: text,
          color: item.color || '#ffbc34',
          icon: item.icon || 'bell',
          ...this.alertTarget(),
        }
        const res = await this.sendAlert(payload)
        const sent = res?.sent ?? 0
        this.ok = true
        this.status = `Sent “${text}” → ${sent} device${sent === 1 ? '' : 's'}`
      } catch (e) {
        this.ok = false
        this.status = e.response?.data?.message || e.message || 'Failed to send'
      } finally {
        this.sending = false
      }
    },
    alertTarget() {
      const payload = {}
      if (this.target && this.target !== 'all') {
        payload.node_id = this.target
      }
      return payload
    },
    async clearAll() {
      if (this.sending) return
      this.sending = true
      this.clearing = true
      this.status = ''
      try {
        const res = await this.sendAlert({ ...this.alertTarget(), action: 'clear' })
        const sent = res?.sent ?? 0
        this.ok = true
        this.status = `Cleared on ${sent} device${sent === 1 ? '' : 's'}`
      } catch (e) {
        this.ok = false
        this.status = e.response?.data?.message || e.message || 'Failed to clear'
      } finally {
        this.sending = false
        this.clearing = false
      }
    },
  },
}
</script>

<style scoped>
.alert-widget {
  color: var(--ws-text);
}
.alert-widget__shortcuts {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  overflow: auto;
  min-height: 0;
}
.alert-widget__shortcut {
  text-align: left;
  white-space: normal;
  line-height: 1.3;
  font-size: 1em;
  font-weight: 600;
}
.alert-widget__shortcut:disabled {
  opacity: 0.65;
}
.alert-widget__clear {
  flex-shrink: 0;
  margin-top: 0.35rem;
  font-weight: 600;
}
</style>
